import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, ClipboardCheck, Search } from 'lucide-react'
import { Link } from 'react-router-dom'
import { useAuth } from '../../context/AuthContext'
import { FieldAppShell } from '../../components/survey/FieldAppShell'
import { isFieldAppHost } from '../../lib/fieldAppHost'
import { getCorrectiveActions } from '../../lib/services/correctiveActions'
import { buildSelfMonitoringSummary, applyFacilityScope, visitStatusBadgeClass } from '../../lib/services/selfMonitoring'
import { getAssignments } from '../../lib/services/visits'
import { getFieldAppUrl } from '../../lib/fieldAppHost'

export function SelfMonitoringDashboardPage() {
  const { viewerContext } = useAuth()
  const scopedFacilityIds = viewerContext?.facility_ids || []
  const fieldAppHost = isFieldAppHost()

  const visitsQuery = useQuery({
    queryKey: ['self-monitoring-visits'],
    queryFn: getAssignments,
  })

  const actionsQuery = useQuery({
    queryKey: ['self-monitoring-actions'],
    queryFn: () => getCorrectiveActions(),
  })

  const scopedVisits = applyFacilityScope(visitsQuery.data || [], scopedFacilityIds)
  const scopedActions = applyFacilityScope(
    (actionsQuery.data || []).map((action) => ({
      ...action,
      facility_id: action.visit?.facility_id || null,
    })),
    scopedFacilityIds,
  )

  const summary = buildSelfMonitoringSummary(scopedVisits, scopedActions)

  if (fieldAppHost) {
    const actions = [
      {
        title: 'Daily Walkthrough',
        description: 'Run the targeted operational check for today.',
        to: '/self-monitoring/daily',
        icon: ClipboardCheck,
      },
      {
        title: 'Document Concern',
        description: 'Capture something you see in the facility workflow.',
        to: '/self-monitoring/checklist',
        icon: AlertTriangle,
      },
      {
        title: 'Guided Survey',
        description: 'Open assigned visits and start a focused survey pass.',
        to: '/facilities',
        icon: Search,
      },
    ]

    return (
      <FieldAppShell
        eyebrow="Field App"
        title="What are you doing?"
        subtitle="Choose a workflow"
        showBackControl={false}
        showNormalAppReturn
        contentClassName="mx-auto w-full max-w-lg space-y-4 px-4 py-4 pb-28"
      >
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-semibold text-amber-800">
          Daily Walkthrough is available, and you can also document an extra concern or start from assigned visits.
        </div>
        <div className="grid gap-3">
          {actions.map((action) => {
            const Icon = action.icon
            return (
              <Link
                key={action.title}
                to={action.to}
                className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm"
              >
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-[#e7f2f7] text-[var(--brand-primary)]">
                  <Icon size={20} />
                </span>
                <span className="min-w-0">
                  <span className="block font-brand text-base font-extrabold text-[var(--brand-deep)]">
                    {action.title}
                  </span>
                  <span className="mt-1 block text-xs font-semibold leading-5 text-slate-600">
                    {action.description}
                  </span>
                </span>
              </Link>
            )
          })}
        </div>
      </FieldAppShell>
    )
  }

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <header className="v11-workspace-header">
        <p className="text-xs font-black uppercase tracking-widest text-[var(--brand-secondary)]">
          Facility operations
        </p>
        <h1 className="font-brand text-2xl font-bold text-[var(--brand-deep)]">Facility Self-Monitoring</h1>
        <p className="mt-1 text-sm text-slate-600">Between-visit checklist execution, overdue action watch, and trend summary cards.</p>
      </header>

      <section className="v11-kpi-grid">
        <div className="v11-kpi-card v11-kpi-accent-rose">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Overdue Actions</p>
          <p className="mt-1 text-3xl font-extrabold text-[var(--brand-deep)]">{summary.overdue_actions}</p>
        </div>
        <div className="v11-kpi-card">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Active Visits</p>
          <p className="mt-1 text-3xl font-extrabold text-[var(--brand-deep)]">{summary.active_visits}</p>
        </div>
        <div className="v11-kpi-card v11-kpi-accent-teal">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Released Visits</p>
          <p className="mt-1 text-3xl font-extrabold text-[var(--brand-deep)]">{summary.released_visits}</p>
        </div>
      </section>

      {/* QAPI Indicators */}
      <section className="v11-section p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">QAPI Indicators</h2>
          <span className="rounded-full bg-teal-100 px-2.5 py-0.5 text-[10px] font-bold uppercase text-teal-700">Self-Monitoring</span>
        </div>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          {[
            { label: 'Infection Control', status: summary.overdue_actions === 0 ? 'On Track' : 'Review', icon: '🔬' },
            { label: 'Medication Safety', status: 'On Track', icon: '💊' },
            { label: 'Life Safety', status: summary.overdue_actions > 0 ? `${summary.overdue_actions} Overdue` : 'On Track', icon: '🔥' },
            { label: 'Patient Rights', status: 'On Track', icon: '👥' },
          ].map(q => (
            <div key={q.label} className="v11-mini-metric p-3">
              <div className="text-xl">{q.icon}</div>
              <p className="mt-1 text-xs font-bold text-slate-800">{q.label}</p>
              <p className={`mt-0.5 text-[10px] font-bold ${q.status === 'On Track' ? 'text-emerald-600' : q.status.includes('Overdue') ? 'text-rose-600' : 'text-amber-600'}`}>
                {q.status}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="v11-section p-5">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-black uppercase tracking-widest text-slate-500">Scoped Visits</h2>
          <a href={getFieldAppUrl('/self-monitoring/checklist')} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">
            Open Checklist Execution
          </a>
        </div>
        {visitsQuery.isLoading && <p className="mt-2 text-sm text-slate-500">Loading visits...</p>}
        {visitsQuery.error && <p className="mt-2 text-sm text-red-600">{(visitsQuery.error as Error).message}</p>}
        {!visitsQuery.isLoading && !visitsQuery.error && scopedVisits.length === 0 && (
          <p className="mt-2 text-sm text-slate-500">No visits available for the current facility scope.</p>
        )}
        <div className="mt-3 grid gap-3">
          {scopedVisits.map((visit) => (
            <div key={visit.id} className="v11-dashboard-card p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-bold text-slate-900">{visit.facility_name}</p>
                <span className={visitStatusBadgeClass(visit.status)}>{visit.status}</span>
              </div>
              <p className="mt-1 text-xs text-slate-500">{visit.visit_date}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  )
}
